import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import client, { extractError } from "@/api";

const STATUS_LABEL = {
  DRAFT: "Draft", PENDING_VERIFICATION: "Pending", VERIFIED: "Verified",
  REJECTED: "Rejected", SUSPENDED: "Suspended",
};

export default function AdminAuditLogsPage() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async (targetPage = page) => {
    setLoading(true); setError("");
    try {
      const res = await client.get(`/admin/audit-logs?page=${targetPage}&page_size=50`);
      setItems(res.data.items || []);
      setPage(res.data.page);
      setTotalPages(res.data.total_pages || 1);
    } catch (err) {
      setError(extractError(err, "Unable to load audit logs."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(1); }, []);

  if (loading) return <div className="loading-block">Loading audit trail…</div>;

  return (
    <div className="admin-audit-page" data-testid="admin-audit-logs-page">
      {error && <div className="form-error">{error}</div>}
      <table className="admin-table" data-testid="audit-log-table">
        <thead>
          <tr>
            <th>When</th><th>Company</th><th>Transition</th><th>Actor</th><th>Reason / Notes</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan={5} className="empty-row">No audit entries yet.</td></tr>}
          {items.map((l) => (
            <tr key={l.id} data-testid={`audit-row-${l.id}`}>
              <td><small>{new Date(l.created_at).toLocaleString()}</small></td>
              <td><strong>{l.company_legal_name || "—"}</strong></td>
              <td>
                <span className={`status-chip status-${(l.previous_status || "draft").toLowerCase()}`}>{STATUS_LABEL[l.previous_status] || l.previous_status || "—"}</span>
                <span className="arrow">→</span>
                <span className={`status-chip status-${l.new_status.toLowerCase()}`}>{STATUS_LABEL[l.new_status] || l.new_status}</span>
              </td>
              <td>{l.actor_email || "system"}<br /><small className="muted">{l.actor_role || "—"}</small></td>
              <td>
                {l.reason && <p className="audit-reason">{l.reason}</p>}
                {l.admin_notes && <p className="audit-notes muted small">Notes: {l.admin_notes}</p>}
                {!l.reason && !l.admin_notes && <span className="muted small">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="pagination" data-testid="audit-pagination">
        <button type="button" className="ghost-button" data-testid="audit-prev-page" onClick={() => load(page - 1)} disabled={page <= 1}>
          <ChevronLeft size={14} /> Previous
        </button>
        <span>Page {page} of {totalPages}</span>
        <button type="button" className="ghost-button" data-testid="audit-next-page" onClick={() => load(page + 1)} disabled={page >= totalPages}>
          Next <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}
